/**
 * AdHocCouponCard — Apr 29, 2026 (Yash).
 *
 * Right-info-panel widget that lets agents create a one-off coupon code on
 * the fly. Unlike the auto-coupon path inside the Place Admin Order form
 * (where we force a "CRM" prefix for audit), this widget lets the agent
 * pick ANY name — even the customer's name — because they're naming it
 * manually for their own bookkeeping.
 *
 * Default expiry: tomorrow. The team agreed 1-day validity is enough since
 * the order is placed in real-time the moment the discount is finalized.
 */
import { useState } from "react";
import { Loader2, Tag, Plus, X, Check } from "lucide-react";
import { toast } from "sonner";
import { websiteOrdersApi } from "../../lib/api";

const tomorrowYmd = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

const AdHocCouponCard = ({ leadId, lead }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createdCode, setCreatedCode] = useState(null);
  const [form, setForm] = useState({
    code: "",
    coupon_type: "d",       // default to %; agents flip with the toggle
    amount: "",
    expiry: tomorrowYmd(),
  });

  const reset = () => {
    setCreatedCode(null);
    setForm({ code: "", coupon_type: "d", amount: "", expiry: tomorrowYmd() });
  };

  const submit = async () => {
    const code = (form.code || "").trim().toUpperCase();
    if (!code) { toast.error("Coupon code is required"); return; }
    if (/\s/.test(code)) { toast.error("Coupon code can't contain spaces"); return; }
    const amount = Number(form.amount);
    if (!amount && amount !== 0) { toast.error("Enter a numeric amount"); return; }
    if (form.coupon_type === "d" && amount > 100) { toast.error("Percent discount can't exceed 100"); return; }

    setSubmitting(true);
    try {
      const res = await websiteOrdersApi.createCoupon({
        code,
        coupon_type: form.coupon_type,
        amount,
        expiry: form.expiry,
        lead_id: leadId,
      });
      setCreatedCode(res.data?.code || code);
      toast.success(`Coupon ${res.data?.code || code} created`, { duration: 6000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create coupon");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-4 py-3 border-t border-[--border]" data-testid="adhoc-coupon-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-pink-500 to-rose-500 text-white shadow-sm">
            <Tag size={13} />
          </div>
          <div>
            <p className="text-[12px] font-extrabold text-[--text-primary] leading-tight">Quick Coupon</p>
            <p className="text-[9px] text-[--text-muted] leading-tight">Create a one-off code on exoticindia.com</p>
          </div>
        </div>
        <button
          onClick={() => { if (open && createdCode) reset(); setOpen((o) => !o); }}
          className="text-[10px] font-bold text-rose-700 hover:text-rose-900 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-rose-50 transition-all"
          data-testid="toggle-adhoc-coupon-btn"
        >
          {open ? <><X size={11} /> Close</> : <><Plus size={11} /> New coupon</>}
        </button>
      </div>

      {createdCode && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2.5 mb-2 flex items-center gap-2" data-testid="adhoc-coupon-success">
          <Check size={14} className="text-emerald-600" />
          <div className="flex-1">
            <p className="text-[11px] font-bold text-emerald-800">Coupon <span className="font-mono">{createdCode}</span> created</p>
            <p className="text-[9px] text-emerald-600">Expires {form.expiry}. Use it on the website checkout.</p>
          </div>
          <button onClick={() => navigator.clipboard?.writeText(createdCode)} className="text-emerald-700 text-[10px] font-bold hover:text-emerald-900" data-testid="adhoc-coupon-copy">Copy</button>
        </div>
      )}

      {open && !createdCode && (
        <div className="space-y-2 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="adhoc-coupon-form">
          <label className="block">
            <span className="text-[9px] font-bold uppercase tracking-wider text-rose-700 block mb-0.5">Coupon code</span>
            <input
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase().replace(/\s+/g, "") }))}
              className="w-full bg-white border border-rose-200 rounded-lg px-2 py-1.5 text-[12px] outline-none focus:ring-1 focus:ring-rose-200 font-mono font-bold"
              placeholder="e.g. RAHUL15"
              data-testid="adhoc-coupon-code-input"
            />
          </label>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-[9px] font-bold uppercase tracking-wider text-rose-700 block mb-0.5">Type</span>
              <div className="flex bg-white border border-rose-200 rounded-lg p-0.5">
                {[{ val: "d", lbl: "%" }, { val: "c", lbl: "$" }].map((o) => (
                  <button
                    key={o.val}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, coupon_type: o.val }))}
                    className={`flex-1 py-1 rounded-md text-[11px] font-bold transition-all ${form.coupon_type === o.val ? "bg-rose-600 text-white shadow-sm" : "text-rose-700 hover:bg-rose-50"}`}
                    data-testid={`adhoc-coupon-type-${o.val}`}
                  >
                    {o.lbl}
                  </button>
                ))}
              </div>
            </div>
            <label className="block">
              <span className="text-[9px] font-bold uppercase tracking-wider text-rose-700 block mb-0.5">
                {form.coupon_type === "d" ? "Discount %" : "Flat amount"}
              </span>
              <input
                type="number"
                min="0"
                max={form.coupon_type === "d" ? "100" : undefined}
                step={form.coupon_type === "d" ? "1" : "0.01"}
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                className="w-full bg-white border border-rose-200 rounded-lg px-2 py-1.5 text-[12px] outline-none focus:ring-1 focus:ring-rose-200 font-mono"
                placeholder={form.coupon_type === "d" ? "15" : "50"}
                data-testid="adhoc-coupon-amount-input"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-[9px] font-bold uppercase tracking-wider text-rose-700 block mb-0.5">Validity (expires on)</span>
            <input
              type="date"
              value={form.expiry}
              min={tomorrowYmd().replace(tomorrowYmd().slice(8), "01")}
              onChange={(e) => setForm((f) => ({ ...f, expiry: e.target.value }))}
              className="w-full bg-white border border-rose-200 rounded-lg px-2 py-1.5 text-[12px] outline-none focus:ring-1 focus:ring-rose-200"
              data-testid="adhoc-coupon-expiry-input"
            />
          </label>

          <button
            onClick={submit}
            disabled={submitting}
            className="w-full bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white py-2 rounded-xl font-extrabold text-[12px] flex items-center justify-center gap-1.5 shadow-sm transition-all disabled:opacity-50"
            data-testid="adhoc-coupon-submit-btn"
          >
            {submitting ? <Loader2 size={13} className="animate-spin" /> : <Tag size={13} />}
            {submitting ? "Creating…" : "Create coupon"}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdHocCouponCard;
